import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One presigned evidence URL entry from the manifest. */
export interface ClosingPackageEvidenceUrl {
  expense_id: number
  file_type: 'receipt' | 'evidence'
  key: string
  url: string
}

/** Minimal embedded WHT expense data for client-side certificate generation. */
export interface ClosingPackageWhtExpense {
  id: number
  expense_date: string
  description: string | null
  amount: number
  wht_rate: number
  wht_amount: number
  vendor_name: string | null
  vendor_tax_id: string | null
  vendor_address: string | null
  property_name: string | null
  property_address: string | null
}

/** The manifest returned by GET /api/closing_package?month=YYYY-MM. */
export interface ClosingPackageManifest {
  month: string
  window_from: string
  window_to: string
  invoices_count: number
  payments_count: number
  expenses_count: number
  expenses_missing_receipt_count: number
  expenses_wht_count: number
  bank_rows_unmatched_count: number
  /** Omitted by older API versions; retain unmatched-count behavior then. */
  bank_statement_coverage?: 'none' | 'partial' | 'full'
  bank_statement_covered_through?: string | null
  // Split by direction: a credit is money in awaiting a Payment, a debit is
  // money out awaiting an Expense. The single total above cannot say which,
  // so a month with no expenses recorded looked complete on the expense line
  // while its transfers out sat unreported.
  bank_rows_unmatched_credits_count: number
  bank_rows_unmatched_debits_count: number
  payments_pending_verification_count: number
  // T149-071/T149-074 (MON-19): standalone payments with no customer and no
  // invoice, over the same payments_scope as payments_count/
  // payments_pending_verification_count (excludes parking, which is a
  // normal already-settled transaction, not money awaiting a payer).
  payments_unidentified_count: number
  evidence_urls: ClosingPackageEvidenceUrl[]
  wht_expenses: ClosingPackageWhtExpense[]
}

// ---------------------------------------------------------------------------
// Query key factory (D-5: one source, never disagree across pages)
// ---------------------------------------------------------------------------

export const closingPackageKeys = {
  all: ['closing_package'] as const,
  month: (month: string) => [...closingPackageKeys.all, month] as const,
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetches the closing-package manifest for a given YYYY-MM month string.
 *
 * Used by both AccountantDashboard (T129-010) and ClosingPackagePage (T129-012)
 * so the two views share one cached response and can never show divergent counts.
 *
 * staleTime: 60 s — the data is summary counts, rarely changes mid-session.
 * enabled: false when month is empty (prevents spurious requests on mount).
 */
export function useClosingPackage(month: string) {
  return useQuery<ClosingPackageManifest>({
    queryKey: closingPackageKeys.month(month),
    enabled: Boolean(month),
    queryFn: async () => {
      const { data } = await api.get<{ data: ClosingPackageManifest }>(
        '/api/closing_package',
        { params: { month } },
      )
      return data.data
    },
    staleTime: 60_000,
  })
}
